import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { apiFetch } from '@/lib/operator-api';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';

// ============================================================================
// Types
// ============================================================================

interface ComplianceMetrics {
  businessId: string;
  date: string;
  messagesWithinWindow: number;
  messagesOutsideWindow: number;
  templateMessagesSent: number;
  rateLimitHits: number;
  activeWindows: number;
  expiredWindows: number;
  violations: number;
}

interface RateLimitStatus {
  messagesUsed: number;
  messagesRemaining: number;
  dailyLimit: number;
  percentUsed: number;
  isLimited: boolean;
  resetAt: string;
}

interface ActiveWindow {
  conversationId: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  windowEnd: string;
  timeRemainingMs: number;
  timeRemainingFormatted: string;
  isExpiringSoon: boolean;
}

interface ComplianceAlert {
  id: string;
  type: string;
  message: string;
  threshold: number;
  currentValue: number;
  createdAt: string;
  acknowledgedAt?: string;
}

interface TemplateUsage {
  templateId: string;
  usageCount: number;
  lastUsedAt: string;
}

interface DashboardData {
  metrics: ComplianceMetrics;
  rateLimitStatus: RateLimitStatus;
  activeWindows: Array<{
    conversationId: string;
    customerId: string;
    windowEnd: string;
    timeRemainingMs: number;
  }>;
  alerts: ComplianceAlert[];
  templateUsage: TemplateUsage[];
}

// ============================================================================
// Component
// ============================================================================

function ComplianceDashboard() {
  const { t } = useTranslation();
  const { operator, selectedBusinessId } = useOperator();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [activeWindows, setActiveWindows] = useState<ActiveWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!operator || !selectedBusinessId) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();

    const fetchDashboardDataInner = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedBusinessId) params.set('businessId', selectedBusinessId);
        const response = await apiFetch(`/api/operator/compliance?${params}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const data = await response.json();

        if (data.success) {
          setDashboardData(data.data);
          setFetchError(null);
        } else {
          const errMsg = typeof data.error === 'string' ? data.error : data.error?.message || 'Failed to fetch compliance data';
          setError(errMsg);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        const errorMsg = err instanceof Error ? err.message : 'Failed to connect to server';
        setError(errorMsg);
        setFetchError(errorMsg);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    const fetchActiveWindowsInner = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedBusinessId) params.set('businessId', selectedBusinessId);
        const response = await apiFetch(`/api/operator/compliance/windows?${params}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const data = await response.json();

        if (data.success) {
          setActiveWindows(data.data.windows);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        // Silently fail - windows are secondary data
      }
    };

    fetchDashboardDataInner();
    fetchActiveWindowsInner();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardDataInner();
      fetchActiveWindowsInner();
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [operator, selectedBusinessId]);

  const fetchDashboardData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBusinessId) params.set('businessId', selectedBusinessId);
      const response = await apiFetch(`/api/operator/compliance?${params}`);
      const data = await response.json();

      if (data.success) {
        setDashboardData(data.data);
        setFetchError(null);
      } else {
        const errMsg = typeof data.error === 'string' ? data.error : data.error?.message || 'Failed to fetch compliance data';
        setError(errMsg);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to connect to server';
      setError(errorMsg);
      setFetchError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveWindows = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBusinessId) params.set('businessId', selectedBusinessId);
      const response = await apiFetch(`/api/operator/compliance/windows?${params}`);
      const data = await response.json();

      if (data.success) {
        setActiveWindows(data.data.windows);
      }
    } catch (err) {
      // Silently fail - windows are secondary data
    }
  };

  const acknowledgeAlert = async (alertId: string) => {
    try {
      const response = await apiFetch('/api/operator/compliance/alerts', {
        method: 'POST',
        body: JSON.stringify({ alertId }),
      });

      if (response.ok) {
        fetchDashboardData();
      }
    } catch (err) {
      // Handle error
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12 text-muted-foreground">{t('compliance.loading')}</div>
      </div>
    );
  }

  if (!selectedBusinessId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight">{t('compliance.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('compliance.subtitle')}</p>
        </header>
        <div className="rounded-lg border p-6 text-center text-muted-foreground">
          {t('common.selectBusiness', 'Please select a business to view compliance data.')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">{error}</div>
      </div>
    );
  }

  const metrics = dashboardData?.metrics;
  const rateLimitStatus = dashboardData?.rateLimitStatus;
  const alerts = dashboardData?.alerts || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <header className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">{t('compliance.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('compliance.subtitle')}
        </p>
      </header>

      {/* Error Banner */}
      {fetchError && (
        <div className="rounded-lg border border-destructive p-4 mb-4">
          <p className="text-destructive text-sm">{fetchError}</p>
        </div>
      )}

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <section className="space-y-4 mb-8">
          <h2 className="text-lg font-semibold">{t('compliance.activeAlerts')}</h2>
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`bg-card rounded-lg border p-4 flex items-start gap-3 ${getAlertSeverity(alert.type)}`}
              >
                <div className="flex-1">
                  <span className="text-xs font-medium uppercase text-muted-foreground">{formatAlertType(alert.type)}</span>
                  <p className="text-sm mt-1">{alert.message}</p>
                  <span className="text-xs text-muted-foreground mt-1 block">
                    {new Date(alert.createdAt).toLocaleString()}
                  </span>
                </div>
                {!alert.acknowledgedAt && (
                  <button
                    className="text-xs text-primary hover:underline mt-2"
                    onClick={() => acknowledgeAlert(alert.id)}
                  >
                    {t('compliance.acknowledge')}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Rate Limit Overview */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-card rounded-lg border p-4 shadow-sm">
          <h3 className="text-sm font-medium mb-2">{t('compliance.dailyMessageQuota')}</h3>
          <div className="space-y-2">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min((rateLimitStatus?.percentUsed || 0) * 100, 100)}%`,
                  backgroundColor: getQuotaColor(rateLimitStatus?.percentUsed || 0),
                }}
              />
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <span>{rateLimitStatus?.messagesUsed || 0}</span>
              <span>/</span>
              <span>{rateLimitStatus?.dailyLimit || 1000}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('compliance.messagesRemaining', { count: rateLimitStatus?.messagesRemaining || 0 })}
          </p>
          {rateLimitStatus?.resetAt && (
            <p className="text-xs text-muted-foreground">
              {t('compliance.resetsAt', { time: new Date(rateLimitStatus.resetAt).toLocaleTimeString() })}
            </p>
          )}
        </div>

        <div className="bg-card rounded-lg border p-4 shadow-sm">
          <h3 className="text-sm font-medium mb-2">{t('compliance.activeWindows')}</h3>
          <div className="text-2xl font-bold">{metrics?.activeWindows || 0}</div>
          <p className="text-sm text-muted-foreground">
            {t('compliance.activeWindowsDescription')}
          </p>
        </div>

        <div className="bg-card rounded-lg border p-4 shadow-sm">
          <h3 className="text-sm font-medium mb-2">{t('compliance.templateMessages')}</h3>
          <div className="text-2xl font-bold">{metrics?.templateMessagesSent || 0}</div>
          <p className="text-sm text-muted-foreground">
            {t('compliance.templatesSentToday')}
          </p>
        </div>

        <div className="bg-card rounded-lg border p-4 shadow-sm">
          <h3 className="text-sm font-medium mb-2">{t('compliance.complianceStatus')}</h3>
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getComplianceStatus(metrics)}`}>
            {getComplianceStatusText(metrics, t)}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {t('compliance.violationsToday', { count: metrics?.violations || 0 })}
          </p>
        </div>
      </section>

      {/* Active Windows List */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-1">{t('compliance.activeMessageWindows')}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t('compliance.activeMessageWindowsDescription')}
        </p>

        {activeWindows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {t('compliance.noActiveWindows')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('compliance.tableCustomer')}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('compliance.tablePhone')}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('compliance.tableTimeRemaining')}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('compliance.tableStatus')}</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">{t('compliance.tableActions')}</th>
                </tr>
              </thead>
              <tbody>
                {activeWindows.map((window) => (
                  <tr key={window.conversationId} className="border-b last:border-0">
                    <td className="py-3 px-4">{window.customerName}</td>
                    <td className="py-3 px-4">{window.customerPhone}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`text-sm ${
                          window.isExpiringSoon ? 'text-destructive font-medium' : ''
                        }`}
                      >
                        {window.timeRemainingFormatted}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {window.isExpiringSoon ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-500">{t('compliance.statusExpiringSoon')}</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-500">{t('compliance.statusActive')}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Link
                        to={`/operator/conversations/${window.conversationId}` as string}
                        className="text-sm text-primary hover:underline"
                      >
                        {t('compliance.viewConversation')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Message Statistics */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4">{t('compliance.todaysStatistics')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-xl font-semibold">
              {metrics?.messagesWithinWindow || 0}
            </div>
            <div className="text-xs text-muted-foreground">{t('compliance.messagesWithinWindow')}</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold">
              {metrics?.messagesOutsideWindow || 0}
            </div>
            <div className="text-xs text-muted-foreground">{t('compliance.templateMessagesOutsideWindow')}</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold">{metrics?.expiredWindows || 0}</div>
            <div className="text-xs text-muted-foreground">{t('compliance.expiredWindows')}</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-semibold">{metrics?.rateLimitHits || 0}</div>
            <div className="text-xs text-muted-foreground">{t('compliance.rateLimitHits')}</div>
          </div>
        </div>
      </section>

      {/* Template Usage */}
      {dashboardData?.templateUsage && dashboardData.templateUsage.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">{t('compliance.templateUsage')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboardData.templateUsage.map((template) => (
              <div key={template.templateId} className="bg-card rounded-lg border p-4 shadow-sm flex flex-col gap-1">
                <span className="text-sm font-medium">{template.templateId}</span>
                <span className="text-sm text-muted-foreground">{t('compliance.templateUses', { count: template.usageCount })}</span>
                <span className="text-xs text-muted-foreground">
                  Last: {new Date(template.lastUsedAt).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

function getQuotaColor(percentUsed: number): string {
  if (percentUsed >= 0.9) return '#ef4444'; // Red
  if (percentUsed >= 0.7) return '#f59e0b'; // Orange
  return '#22c55e'; // Green
}

function getAlertSeverity(type: string): string {
  if (type.includes('limit')) return 'border-yellow-500/50 bg-yellow-50 dark:bg-yellow-900/10';
  if (type.includes('violation')) return 'border-destructive/50 bg-destructive/5';
  return 'border-blue-500/50 bg-blue-50 dark:bg-blue-900/10';
}

function formatAlertType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getComplianceStatus(metrics: ComplianceMetrics | undefined): string {
  if (!metrics) return 'bg-muted text-muted-foreground';
  if (metrics.violations > 0) return 'bg-destructive/10 text-destructive';
  if (metrics.rateLimitHits > 0) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-500';
  return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-500';
}

function getComplianceStatusText(metrics: ComplianceMetrics | undefined, t: (key: string) => string): string {
  if (!metrics) return t('compliance.statusUnknown');
  if (metrics.violations > 0) return t('compliance.statusViolations');
  if (metrics.rateLimitHits > 0) return t('compliance.statusRateLimited');
  return t('compliance.statusCompliant');
}

export const Route = createFileRoute('/_auth/operator/compliance')({
  component: ComplianceDashboard,
});
